import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = {
  "title": "Component Contracts Are Where Taste Becomes Infrastructure",
  "slug": "component-contracts-are-where-taste-becomes-infrastructure",
  "engagement": "design-system-mcp",
  "publishedAt": "2026-06-11T08:00:00.000Z",
  "readTime": "14 min read",
  "excerpt": "Taste does not scale by explanation alone. In agentic design systems, it has to become contracts, examples and verification.",
  "seoTitle": "Component Contracts: Where Taste Becomes Infrastructure",
  "authorName": "Petri Lahdelma",
  "authorSlug": "petri-lahdelma",
  "status": "scheduled",
  "series": "Agentic Design Systems",
  "tags": ["Component Contracts", "Design Systems", "AI", "Frontend Architecture"],
  "mainImageUrl": "/blog/agentic-design-systems/hero-03-contracts.png",
  "mainImageAlt": "Split panel: documentation explains, contracts constrain with v1 and v2 layers"
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    figcaption: "figcaption",
    figure: "figure",
    h2: "h2",
    h3: "h3",
    hr: "hr",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ..._provideComponents(),
    ...props.components
  }, {ArticleFigure} = _components;
  if (!ArticleFigure) _missingMdxReference("ArticleFigure", true);
  return _jsxs(_Fragment, {
    children: [_jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "/blog/from-tokens-to-thinking-systems-making-ai-native-design-systems-actually-work",
        children: "From Tokens to Thinking Systems"
      }), " argued that LLMs follow rules when rules are explicit, scoped and validated. This post is where that argument meets CI."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Thesis:"
      }), " Taste that lives only in senior designers’ heads is a bottleneck, not a design system. Contracts are taste with handles: precise enough for agents, validators and new teammates to apply without guessing. Documentation explains why; contracts constrain what may ship. If the forbidden combination does not fail a command, you still debug taste in production."]
    }), "\n", _jsx(_components.p, {
      children: "A senior designer can glance at a screen and name what is wrong: hierarchy is noisy, spacing belongs to three systems, the primary action fights the destructive one, the empty state performs optimism instead of helping. That judgment is taste. When taste stays informal, agents treat every valid prop combination as equally acceptable. When taste becomes infrastructure, repeat failures stop being review comments and start being red CI."
    }), "\n", _jsxs(_components.p, {
      children: ["If you cannot validate generated UI mechanically, you will debug it in production. That is the link to ", _jsx(_components.a, {
        href: "https://www.petrilahdelma.com/writing/design-systems-for-bots",
        children: "design systems for bots"
      }), " and the ", _jsx(_components.a, {
        href: "/blog/agentic-design-systems-need-operating-models-not-more-components",
        children: "operating model"
      }), " from Part 1."]
    }), "\n", _jsx(_components.h2, {
      children: "Documentation explains. Contracts constrain."
    }), "\n", _jsxs(ArticleFigure, {
      layout: "full",
      children: [_jsx("img", {
        src: "/blog/agentic-design-systems/fig-contract-layers.webp",
        alt: "Documentation explains intent; contracts constrain usage with structural and semantic layers."
      }), _jsx("figcaption", {
        children: "Documentation carries judgment; contracts carry boundaries. In a healthy system, the boundary is what fails a command."
      })]
    }), "\n", _jsx(_components.p, {
      children: "Component docs describe purpose, props, variants, examples, dos and don’ts. That is essential for humans and insufficient for agents. Agents need allowed variants, required states, forbidden combinations, deprecated props, accessibility guarantees, lifecycle status and what must exist in Storybook before a component is treated as stable. Documentation carries judgment; contracts carry boundaries. In a healthy system, the boundary is what fails a command."
    }), "\n", _jsxs(_components.p, {
      children: ["Humans read docs when they are curious or stuck. Agents read whatever is shortest to copy. If the doc says “avoid using danger buttons for non-destructive actions” and the contract allows ", _jsx(_components.code, {
        children: "variant=\"danger\""
      }), " next to ", _jsx(_components.code, {
        children: "variant=\"primary\""
      }), " with no complaint, the agent will eventually place both in a delete confirmation. The doc was right. The system was not enforceable."]
    }), "\n", _jsx(_components.h3, {
      children: "What we ship today (v1 structural)"
    }), "\n", _jsxs(_components.p, {
      children: ["Our repo has 127 ", _jsx(_components.code, {
        children: ".contract.json"
      }), " files. Here is a real excerpt from ", _jsx(_components.code, {
        children: "Button.contract.json"
      }), ", what ", _jsx(_components.code, {
        children: "npm run validate:components"
      }), " checks today:"]
    }), "\n", _jsxs(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: [_jsx(_components.figcaption, {
        "data-rehype-pretty-code-title": "",
        "data-language": "json",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: "Button.contract.json (shipped)"
      }), _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "json",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "json",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"name\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Button\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"tier\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"molecule\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"status\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"beta\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"requiredStories\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Default\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Playground\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Example\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"ForcedColors\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "],"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"a11y\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"keyboard\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Enter\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"Space\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "],"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"ariaRequirements\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"accessible name\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"aria-busy when loading\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"disabled state\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "],"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"reviewed\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#023B95",
                "--shiki-dark": "#79C0FF",
                "--shiki-hcw": "#023B95",
                "--shiki-hcb": "#91CBFF"
              },
              children: "true"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"forcedColorsVerified\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#023B95",
                "--shiki-dark": "#79C0FF",
                "--shiki-hcw": "#023B95",
                "--shiki-hcb": "#91CBFF"
              },
              children: "true"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  },"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"tokens\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"colors\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"--color-warning-text\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"--color-white\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "]"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "}"
            })
          })]
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "This enforces structural alignment: props match CVA, required stories exist, a11y flags are set, token references are declared. It does not yet fail when primary and danger buttons sit side by side in a delete confirmation. That gap is honest. Structural contracts catch drift in APIs and stories. Semantic contracts catch drift in product judgment."
    }), "\n", _jsxs(_components.p, {
      children: ["Structural validation already pays for itself. When someone renames a story from ", _jsx(_components.code, {
        children: "Example"
      }), " to ", _jsx(_components.code, {
        children: "Showcase"
      }), " without updating the contract, CI fails before design review. When a new prop lands in code but not in CVA, the validator catches the mismatch. When a component claims forced-colors verification but no story exercises it, the flag is not decorative; it is checked."]
    }), "\n", _jsx(_components.h3, {
      children: "What we are migrating toward (v2 semantic)"
    }), "\n", _jsx(_components.p, {
      children: "The next layer adds product rules, the ones that cause repeat drift:"
    }), "\n", _jsxs(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: [_jsx(_components.figcaption, {
        "data-rehype-pretty-code-title": "",
        "data-language": "json",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: "Button.pattern-rules.target.json"
      }), _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "json",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "json",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"patternRules\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"actionHierarchy\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "      \"primary\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"one per decision region\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "      \"danger\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"destructive or irreversible actions only\""
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    },"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#024C1A",
                "--shiki-dark": "#7EE787",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "    \"forbidden\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "      \"primary and danger competing in the same action group\""
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    ]"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "}"
            })
          })]
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["The schema matters less than the move from recommendation to constraint and honesty about which layer fails a command today. I ship tooling as ", _jsx(_components.a, {
        href: "https://www.npmjs.com/package/@petritapanilahdelma/llm-component-contracts",
        children: _jsx(_components.code, {
          children: "llm-component-contracts"
        })
      }), " and ", _jsx(_components.a, {
        href: "https://www.npmjs.com/package/@petritapanilahdelma/llm-component-cli",
        children: _jsx(_components.code, {
          children: "llm-component-cli"
        })
      }), ": validate, migrate, drift-check, scaffold. When ", _jsx(_components.code, {
        children: "drift-check"
      }), " fails because Storybook args no longer match the contract, documentation stops being optional."]
    }), "\n", _jsx(_components.p, {
      children: "Semantic rules start from repeat review comments. If three PRs in a month get “only one primary in this footer,” that sentence belongs in a contract. If agents keep putting icon-only buttons without labels in toolbars, the contract should require an accessible name story and fail promotion without it. If empty states always forget recovery copy, encode minimum content structure in the pattern recipe and reference it from the Alert or EmptyState contract."
    }), "\n", _jsx(_components.p, {
      children: "You do not need the perfect schema on day one. You need one forbidden combination that fails. Ship that, watch false positives, tighten language, add the next rule."
    }), "\n", _jsx(_components.h2, {
      children: "The failure mode of soft rules"
    }), "\n", _jsx(_components.p, {
      children: "Soft rules feel clear until a machine applies them. “Use cards for grouped content” does not say grouped how. “Keep primary actions clear” does not say how many primaries per region. “Write concise errors” can still be useless without recovery."
    }), "\n", _jsx(_components.p, {
      children: "Contracts ask for sharper structure: a card when content shares an object boundary or decision context, not when layout feels busy; one primary per decision region unless a human approves an exception; an error that includes cause, recovery and next action when the user can act. Encode the decisions that create repeat drift: action hierarchy, token scope, destructive patterns, form anatomy, empty states without recovery. Leave proportion, brand warmth and narrative timing to human review."
    }), "\n", _jsx(_components.p, {
      children: "I have watched agents place primary and danger buttons side by side in a delete flow (valid props, invalid product design) because nothing in the repo forbade the combination yet. Structural contracts pass; product judgment fails. Contracts become shared memory only when the forbidden combination actually fails a check."
    }), "\n", _jsx(_components.p, {
      children: "Soft rules also fail across locales and density modes. “Keep it airy” means something different on a mobile settings screen than on a data table toolbar. Contracts anchor on testable structure: minimum touch target, maximum actions per row, required heading level for dialog titles. Judgment fills the gap between minimum and excellent."
    }), "\n", _jsx(_components.h2, {
      children: "A walkthrough: delete workspace"
    }), "\n", _jsxs(_components.p, {
      children: ["Consider a destructive flow: delete workspace. Intent is clear. The agent assembles ", _jsx(_components.code, {
        children: "Dialog"
      }), ", ", _jsx(_components.code, {
        children: "Button"
      }), " and copy from nearby examples."]
    }), "\n", _jsx(_components.p, {
      children: "Without semantic contracts, you might get: primary “Delete workspace” and secondary “Cancel” (acceptable) or primary “Delete” next to danger “Delete forever” because two stories used different variant names; that is unacceptable but valid at the prop layer. With semantic rules, the action group fails when two competing commit variants appear, when danger is used without irreversibility copy or when the dialog lacks a focused heading for screen readers."
    }), "\n", _jsx(_components.p, {
      children: "The fix is not “prompt harder.” The fix is a contract on the dialog pattern and button composition that the validator or a lightweight AST check can enforce. Human review then spends time on whether the warning copy matches brand voice, not on whether the hierarchy is legal."
    }), "\n", _jsxs(_components.p, {
      children: ["Pattern-level contracts can reference component contracts without duplicating them. A ", _jsx(_components.code, {
        children: "DestructiveFlow.recipe.json"
      }), " might require ", _jsx(_components.code, {
        children: "Dialog"
      }), " with titled heading, ", _jsx(_components.code, {
        children: "Button"
      }), " danger variant for commit and explicit absence of a second primary in the action region. Validators can stay dumb at first: even a checklist skill that fails the PR description when fields are missing moves you from soft to operational."]
    }), "\n", _jsx(_components.h2, {
      children: "Contracts improve human review"
    }), "\n", _jsx(_components.p, {
      children: "Good contracts do not flatten judgment; they protect it. When validation catches wrong token tier, missing required story or broken accessibility expectation, review stops wasting time on preventable drift. Conversation moves to what still requires taste: fit to the user goal, cognitive load, whether an exception is worth the complexity."
    }), "\n", _jsx(_components.p, {
      children: "Before contracts, a typical design-system review mixed three conversations: API correctness, pattern correctness and aesthetic fit. After structural contracts, the first two move to CI. Reviewers arrive with fewer “did you run the checker?” comments and more “should we ship this exception?” questions. That is the job design leads actually want."
    }), "\n", _jsxs(_components.p, {
      children: ["A contract nobody validates is another doc. Our validator lives in ", _jsx(_components.code, {
        children: "scripts/design-system/validate-components.ts"
      }), ". In practice that means ", _jsx(_components.code, {
        children: "npm run validate:components"
      }), ", axe in CI, Stylelint with token rules, drift reports from ", _jsx(_components.code, {
        children: "llm-component-cli"
      }), ". Uncomfortable at first, then faster, because debate shifts from “did we follow the doc?” to “is this exception justified?”"]
    }), "\n", _jsx(_components.h2, {
      children: "What to encode (and what to leave human)"
    }), "\n", _jsx(_components.p, {
      children: "Encode token scopes, variant rules, state models, accessibility guarantees, composition anti-patterns, lifecycle stages, required stories and review triggers. Leave proportion, emotional fit and the sense that something is technically correct but wrong for the moment. The strongest systems do not automate taste. They protect the conditions where taste is applied at the right altitude."
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Encode in contracts"
          }), _jsx(_components.th, {
            children: "Keep in human review"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Forbidden variant pairs"
          }), _jsx(_components.td, {
            children: "Whether the headline tone fits the brand"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Required stories per state"
          }), _jsx(_components.td, {
            children: "Whether the illustration helps or distracts"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Token tier per component"
          }), _jsx(_components.td, {
            children: "Spacing rhythm in marketing hero sections"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Lifecycle status (beta/stable)"
          }), _jsx(_components.td, {
            children: "Whether to ship an experiment at all"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Accessibility guarantees"
          }), _jsx(_components.td, {
            children: "Copy warmth vs directness in sensitive flows"
          })]
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "When in doubt, ask: has this mistake happened three times? If yes, encode. If no, document and wait. Over-contracting early creates false positives that teams ignore. Under-contracting creates agents that learn the wrong defaults."
    }), "\n", _jsx(_components.h2, {
      children: "Migrating from docs to contracts"
    }), "\n", _jsx(_components.p, {
      children: "Start with five components that agents misuse most. Export their current Storybook stories and props into structural contracts. Run validation in CI in report-only mode for one sprint if you must, then flip to blocking. Collect review comments that repeat. Promote the first semantic rule that would have caught a real bug. Link the contract from the doc. Docs become commentary on the enforceable baseline, not a parallel truth."
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(_components.a, {
        href: "/blog/the-minimum-viable-agent-ready-design-system",
        children: "minimum viable stack"
      }), " in Part 4 assumes this migration path. You do not need one hundred contracts on day one. You need five that fail for the right reasons."]
    }), "\n", _jsx(_components.h2, {
      children: "Working with validators that complain"
    }), "\n", _jsx(_components.p, {
      children: "Structural validators feel picky until they save a release. Common pushback and responses:"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "“The contract is too strict for our velocity.”"
      }), " Start report-only in CI for one sprint. Log failures. If fewer than ten percent would have blocked bad merges, tighten later. If more than half would have helped, turn blocking on."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "“Design needs freedom.”"
      }), " Contracts encode minimums, not maximums. Human review still owns proportion and voice. If designers need an exception, add a review trigger and document it; do not silently bypass the validator."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "“Agents keep failing the same rule.”"
      }), " Good. Fix the example or the skill, not the rule. If humans fail it too, the rule is real."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "“We have two repos.”"
      }), " Contracts follow components. If agents touch both repos, harness scope and validation commands must exist in both. Otherwise one repo must be declared read-only to agents."]
    }), "\n", _jsx(_components.h2, {
      children: "Contracts and the operating loop"
    }), "\n", _jsxs(_components.p, {
      children: ["Contracts sit in the ", _jsx(_components.strong, {
        children: "rules"
      }), " and ", _jsx(_components.strong, {
        children: "evidence"
      }), " stages of the loop from Part 1. Rules state what may exist; evidence proves stories, a11y and tokens match. When validation fails, better rules follow: a new forbidden pair, a required story for loading state, a lifecycle flag that marks beta components as unsafe defaults for generation."]
    }), "\n", _jsx(_components.p, {
      children: "Without closing that loop, contracts become PDFs. With it, every failed PR teaches the system something agents inherit on the next run."
    }), "\n", _jsx(_components.h2, {
      children: "Field notes from validation work"
    }), "\n", _jsxs(_components.p, {
      children: ["When we first wired ", _jsx(_components.code, {
        children: "validate:components"
      }), " into CI, the noise was instructive. Missing ", _jsx(_components.code, {
        children: "ForcedColors"
      }), " stories: easy fix, embarrassing gap. Token lists that referenced deprecated CSS variables: harder fix, revealed Figma-code drift. Components marked ", _jsx(_components.code, {
        children: "a11y.reviewed: true"
      }), " without keyboard stories: process problem, not a linter problem. Each failure class pointed to a different owner: design for examples, engineering for props, shared for honesty in contract flags."]
    }), "\n", _jsxs(_components.p, {
      children: ["Drift-check via ", _jsx(_components.code, {
        children: "llm-component-cli"
      }), " caught Storybook args that no longer matched contract enums, the kind of silent rot agents amplify because args look like API documentation. Treat drift reports like production incidents for agentic work. If args lie, agents learn the wrong prop names with confidence."]
    }), "\n", _jsx(_components.p, {
      children: "Semantic rules will introduce false positives. Plan for them. A rule that forbids adjacent primary and danger buttons might flag a legitimate marketing hero with two CTAs: rare in product UI, common in marketing. Encode scope: pattern recipes for product surfaces vs campaign surfaces. Contracts without scope become folklore agents ignore after the third false alarm."
    }), "\n", _jsx(_components.h2, {
      children: "Encoding lifecycle so agents know what to trust"
    }), "\n", _jsxs(_components.p, {
      children: ["Every contract should answer: ", _jsx(_components.strong, {
        children: "may an agent treat this component as a default?"
      }), " Experimental components need ", _jsx(_components.code, {
        children: "status: experimental"
      }), " and missing promotion stories; validators should warn when agents import them in product paths. Beta components might be copyable with caution flags. Stable components carry the full story matrix and semantic rules."]
    }), "\n", _jsx(_components.p, {
      children: "Deprecation is where systems usually lie. A prop marked deprecated in a comment but still featured in the Default story is an agent trap. Deprecation belongs in the contract, the story names and a migration snippet in the same folder. Agents read files sequentially; put the warning where the copy path starts."
    }), "\n", _jsx(_components.h2, {
      children: "A contract authoring workflow that sticks"
    }), "\n", _jsx(_components.p, {
      children: "When a designer flags a repeat issue in critique, capture the sentence verbatim. If it applies to a component API, open the contract in the same sprint, not a doc ticket for later. If it applies to composition, open the pattern recipe. Assign an engineering partner to wire validation or admit honestly that the rule is guidance-only until v2 schema lands."
    }), "\n", _jsx(_components.p, {
      children: "Review contracts in PR like code. A contract change without a story change is suspicious. A story change without a contract change is drift waiting to happen. Pair them the way you pair API and tests."
    }), "\n", _jsx(_components.p, {
      children: "Publish a one-page “contract changelog” when semantic rules ship so squads know what newly fails. Silence creates resentment; loud failures create learning, provided false positives are fixed quickly."
    }), "\n", _jsxs(_components.p, {
      children: ["When ", _jsx(_components.code, {
        children: "npm run validate:components"
      }), " fails, the PR author should see which field failed and a link to the example that passes. Good error messages are part of agentic UX for humans too."]
    }), "\n", _jsx(_components.h2, {
      children: "Thesis recap"
    }), "\n", _jsx(_components.p, {
      children: "Documentation persuades; contracts enforce. Structural contracts align APIs and stories; semantic contracts protect product judgment. Agents inherit whatever fails or passes in CI. Plan accordingly. The minimum stack in Part 4 gives you scope; AgentOps in Part 5 gives you ownership. The work is not finished when the schema is clever. It is finished when the forbidden combination fails on a real PR."
    }), "\n", _jsxs(_components.p, {
      children: ["If you remember one sentence from this post: ", _jsx(_components.strong, {
        children: "taste that does not fail a command is still just a comment."
      }), " Everything else is implementation detail you can ship incrementally."]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Series:"
      }), " Part 3 of 5, ", _jsx(_components.strong, {
        children: "←"
      }), " ", _jsx(_components.a, {
        href: "/blog/tools-harnesses-and-skills-the-missing-model-for-design-systems",
        children: "Tools, Harnesses and Skills"
      }), ", ", _jsx(_components.strong, {
        children: "→"
      }), " ", _jsx(_components.a, {
        href: "/blog/the-minimum-viable-agent-ready-design-system",
        children: "Minimum Viable Agent-Ready"
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
